import { LiquidSurface } from '@repo/ui/components/liquid-surface';
import { Skeleton } from '@repo/ui/components/skeleton';
import { cn } from '@repo/ui/lib/utils';
import { AnimatedValue } from '@/components/animated-value';

import type {
  InvestorHoldingsResponse,
  InvestorRiskMetrics
} from '../types/api';
import { formatBasisPoints } from '@/lib/format/percent/basis-points';
import { formatPercent } from '@/lib/format/percent/percent';
import { formatCompactUsd } from '@/lib/format/usd/compact-usd';
import {
  formatHoldingsTotal,
  valuationNotes,
  valueHoldings,
  WALLET_TRACKING_OFF,
  walletTokensUntracked,
  type CurrencyRegistry
} from '../utils/holding-format';

interface PortfolioSnapshotProps {
  holdings?: InvestorHoldingsResponse;
  /** Names for strategy currency addresses, resolved by the view. */
  currencies?: CurrencyRegistry;
  metrics?: InvestorRiskMetrics;
  holdingsLoading?: boolean;
  holdingsError?: boolean;
  metricsLoading?: boolean;
  metricsError?: boolean;
}

interface SnapshotTile {
  label: string;
  value: string | null;
  meta: string;
  /**
   * One plain sentence about the value, when the figure needs it: what a
   * dollar total leaves out, what a served total adds unconverted, or a
   * stale rate behind it.
   */
  note?: string;
  isLoading: boolean;
  isError: boolean;
  /** A loss reads red — bad signals never hide in neutral ink. */
  isBad?: boolean;
}

function TileValue({ tile }: { tile: SnapshotTile }) {
  if (tile.isLoading) return <Skeleton className="h-8 w-24" />;
  if (tile.isError || tile.value == null)
    return <span className="text-rowa-text-tertiary">—</span>;
  return <AnimatedValue value={tile.value} />;
}

/**
 * Portfolio snapshot (O03, node 1792:497): the four live figures that give
 * the score its context — portfolio value, YTD net return, invested capital,
 * and idle capital.
 */
export function PortfolioSnapshot({
  holdings,
  currencies,
  metrics,
  holdingsLoading = false,
  holdingsError = false,
  metricsLoading = false,
  metricsError = false
}: PortfolioSnapshotProps) {
  // The portfolio value on one stated basis (`valueHoldings`): the sum of
  // the converted rows when the whole wallet is in the response, the served
  // total when it spans more than one page. A share of it is only printed
  // when that total is every holding at its dollar value; a served total
  // that adds a euro vault in at its own amount, or a sum with a row left
  // out for lack of a rate, is not something to take a percentage of.
  const valuation = holdings ? valueHoldings(holdings, currencies) : null;
  const covered = valuation?.total.reconciled ? valuation.total.usd : null;
  // The value is a plain dollar fact only when it is reconciled and every
  // rate behind it is current. Otherwise its first note says why not.
  const valueNote =
    valuation && (!valuation.total.reconciled || valuation.usedStaleRate)
      ? valuationNotes(valuation)[0]
      : undefined;
  // Cash rows are wallet tokens, natively priced in dollars. With the wallet
  // scan off or out, the served "0.00" was never measured: the tile says
  // the figure is not tracked rather than print $0.
  const untracked = walletTokensUntracked(holdings?.coverage);
  const cash =
    holdings && untracked == null ? Number(holdings.totals.cashValueUsd) : null;
  const invested = metrics?.investedCapital ?? null;
  // Ratio rule for invested capital: it comes from /metrics, which is always
  // rowa-managed, so "% of portfolio value" renders only when /holdings
  // declares populationScope 'rowa-managed' too (v1.89.0). A rowa-managed
  // numerator over a wallet-merged (or undeclared) total is not a
  // like-for-like share, and falls back to the plain description.
  //
  // Idle capital needs no such gate: cash and the covered total are two
  // figures of the same /holdings response, over the same population,
  // whatever that population is. Gating it hid the idle share exactly when
  // the wallet's own tokens are counted, which is when it matters most.
  const likeForLike = holdings?.populationScope === 'rowa-managed';
  // Same plain-words population note as the NAV card's provenance.
  const scopeNote =
    holdings?.populationScope === 'wallet-merged'
      ? ' · includes external wallet holdings'
      : '';

  const tiles: SnapshotTile[] = [
    {
      label: 'Portfolio value',
      value: valuation ? formatHoldingsTotal(valuation) : null,
      meta: !holdings
        ? 'Awaiting holdings'
        : // Null coverage means ROWA has nothing to measure — an empty
          // portfolio is the launch default. "0% priced" would read as
          // broken pricing on holdings that do not exist.
          holdings.coverage.pricedBps == null
          ? holdings.coverage.holdingsTotal === 0
            ? 'No positions yet'
            : 'Waiting on pricing data'
          : `${formatBasisPoints(holdings.coverage.pricedBps, { fractionDigits: 0 })} priced · ${holdings.coverage.holdingsTotal} holding${holdings.coverage.holdingsTotal === 1 ? '' : 's'}${scopeNote}`,
      note: valueNote,
      isLoading: holdingsLoading,
      isError: holdingsError
    },
    {
      label: 'Net return',
      value:
        metrics?.ytdPerformanceBps != null
          ? `${formatBasisPoints(metrics.ytdPerformanceBps, {
              signed: true,
              fractionDigits: 1
            })} YTD`
          : null,
      // Vault fees reach the share price only when they are charged.
      meta: 'Adjusted for deposits and withdrawals · after fees charged so far',
      isLoading: metricsLoading,
      isError: metricsError,
      isBad: metrics?.ytdPerformanceBps != null && metrics.ytdPerformanceBps < 0
    },
    {
      label: 'Invested capital',
      value:
        invested != null
          ? formatCompactUsd(invested, { maximumFractionDigits: 2 })
          : null,
      meta:
        likeForLike && invested != null && covered != null && covered > 0
          ? `${formatPercent(invested / covered, { from: 'fraction', digits: 1 })} of portfolio value`
          : 'Net deposits into ROWA vaults',
      isLoading: metricsLoading,
      isError: metricsError
    },
    {
      label: 'Idle capital',
      value:
        cash != null
          ? formatCompactUsd(cash, { maximumFractionDigits: 2 })
          : null,
      meta:
        untracked === 'disabled'
          ? `Not tracked. ${WALLET_TRACKING_OFF}.`
          : untracked === 'unavailable'
            ? "Not tracked right now. We can't read your wallet tokens."
            : cash != null && covered != null && covered > 0
              ? `${formatPercent(cash / covered, { from: 'fraction', digits: 1 })} of portfolio value in cash`
              : 'Cash and stablecoins in the wallet',
      isLoading: holdingsLoading,
      isError: holdingsError
    }
  ];

  // Embedded inside the health card as its outcomes strip — the composed
  // Overview keeps outcomes and practices on one hero surface, unblended.
  return (
    <section aria-label="Portfolio snapshot">
      <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map((tile) => (
          <LiquidSurface key={tile.label} type="inset" className="p-4">
            <dt className="text-rowa-label-sm text-rowa-text-brand tracking-[0.08em] uppercase">
              {tile.label}
            </dt>
            <dd
              className={cn(
                'text-rowa-data-large mt-2 tabular-nums',
                tile.isBad ? 'text-rowa-status-error' : 'text-rowa-text-primary'
              )}
            >
              <TileValue tile={tile} />
            </dd>
            <dd className="text-rowa-label-sm text-rowa-text-secondary mt-1.5">
              {tile.meta}
            </dd>
            {tile.note && !tile.isLoading && !tile.isError ? (
              <dd className="text-rowa-label-sm text-rowa-text-tertiary mt-1">
                {tile.note}
              </dd>
            ) : null}
          </LiquidSurface>
        ))}
      </dl>
    </section>
  );
}
